import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  BarChart3,
  Calendar,
  RefreshCw,
  Download,
  Filter,
  ChevronDown,
  ExternalLink,
  Play,
  Image as ImageIcon
} from 'lucide-react';
import Button from '../../components/ui/Button';
import {
  SocialPlatform,
  PLATFORM_CONFIGS,
  ContentItem
} from '../../types/contentHub';
import { contentHubService } from '../../services/contentHubService';
import { useProject } from '../../contexts/ProjectContext';

// Platform Icon Component
const PlatformIcon: React.FC<{ platform: SocialPlatform; size?: number }> = ({ platform, size = 20 }) => {
  const config = PLATFORM_CONFIGS[platform];
  const icons: Record<SocialPlatform, React.ReactNode> = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    twitter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    youtube: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    youtube_shorts: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 14.65v-5.3L15 12l-5 2.65zm7.77-4.33c-.77-.32-1.2-.5-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.24-2.53-5.07-1.56L6 6.94c-1.29.68-2.07 2.04-2 3.49.07 1.42.93 2.67 2.22 3.25.03.01 1.2.5 1.2.5L6 14.93c-1.83.97-2.53 3.24-1.56 5.07.97 1.83 3.24 2.53 5.07 1.56l8.5-4.5c1.29-.68 2.06-2.04 1.99-3.49-.07-1.42-.94-2.68-2.23-3.25z"/>
      </svg>
    ),
    tiktok: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
    threads: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.106-1.146 3.451-1.175 1.016-.022 1.96.139 2.818.468-.073-.938-.306-1.683-.7-2.216-.453-.614-1.143-.936-2.053-.958l-.023-.003c-.689 0-1.58.142-2.433.907l-1.416-1.486c1.249-1.12 2.664-1.538 4.001-1.538h.036c1.478.034 2.689.548 3.6 1.528.857.922 1.366 2.2 1.514 3.8.475.19.917.422 1.32.695 1.198.813 2.074 1.922 2.533 3.205.752 2.106.593 4.932-1.635 7.11C18.581 23.087 15.994 23.973 12.186 24z"/>
      </svg>
    )
  };

  return <span style={{ color: config.color }}>{icons[platform]}</span>;
};

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, change, icon, color }) => (
  <div className="min-w-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-5">
    <div className="flex items-center justify-between mb-2 sm:mb-3">
      <div className={`p-1.5 sm:p-2.5 rounded-lg ${color}`}>
        {icon}
      </div>
      {/* Only show a trend when there is a real, non-zero change from the API */}
      {change !== undefined && change !== 0 && (
        <div className={`flex items-center gap-1 text-sm font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          {Math.abs(change)}%
        </div>
      )}
    </div>
    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1 break-words">{title}</p>
  </div>
);

// Platform Stats Row
const PlatformStatsRow: React.FC<{
  platform: SocialPlatform;
  followers: number;
  posts: number;
  engagement: number;
  reach: number;
  growth: number;
  labels: { followers: string; posts: string; engagement: string; reach: string; growth: string };
}> = ({ platform, followers, posts, engagement, reach, growth, labels }) => {
  const config = PLATFORM_CONFIGS[platform];

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex items-center gap-3 sm:w-48 min-w-0">
        <div className={`p-2 rounded-lg flex-shrink-0 ${config.bgColor}`}>
          <PlatformIcon platform={platform} size={20} />
        </div>
        <span className="font-medium text-gray-900 dark:text-white truncate">{config.name}</span>
      </div>
      <div className="flex-1 min-w-0 grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 text-center">
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{followers.toLocaleString()}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{labels.followers}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{posts}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{labels.posts}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{engagement.toFixed(1)}%</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{labels.engagement}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{reach.toLocaleString()}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{labels.reach}</p>
        </div>
        <div>
          <p className={`font-semibold ${growth > 0 ? 'text-green-600' : growth < 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {growth === 0 ? '—' : `${growth > 0 ? '+' : ''}${growth}%`}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{labels.growth}</p>
        </div>
      </div>
    </div>
  );
};

// Top Post Card
const TopPostCard: React.FC<{
  post: {
    id: string;
    platform: SocialPlatform;
    content: string;
    imageUrl?: string;
    likes: number;
    comments: number;
    shares: number;
    reach: number;
    publishedAt: Date;
  };
}> = ({ post }) => {
  const config = PLATFORM_CONFIGS[post.platform];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow">
      {post.imageUrl ? (
        <div className="aspect-video bg-gray-100 relative">
          <img src={post.imageUrl} alt="" className="w-full h-full object-cover" />
          <div className="absolute top-2 left-2">
            <div className={`p-1.5 rounded-lg ${config.bgColor}`}>
              <PlatformIcon platform={post.platform} size={16} />
            </div>
          </div>
        </div>
      ) : (
        <div className="aspect-video bg-gray-100 flex items-center justify-center">
          <ImageIcon className="w-12 h-12 text-gray-300" />
        </div>
      )}
      <div className="p-4">
        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 mb-3">{post.content}</p>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-3 sm:gap-4 text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <Heart className="w-4 h-4" />
              {post.likes.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />
              {post.comments.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <Share2 className="w-4 h-4" />
              {post.shares.toLocaleString()}
            </span>
          </div>
          <span className="text-gray-400 text-xs">
            {new Date(post.publishedAt).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  );
};

// Simple Bar Chart Component
const SimpleBarChart: React.FC<{
  data: { label: string; value: number; color: string }[];
  maxValue?: number;
}> = ({ data, maxValue }) => {
  const max = maxValue || Math.max(...data.map(d => d.value));

  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className="text-sm text-gray-600 dark:text-gray-400 w-16 sm:w-20 truncate flex-shrink-0">{item.label}</span>
          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${(item.value / max) * 100}%`,
                backgroundColor: item.color
              }}
            />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white w-12 sm:w-16 text-right flex-shrink-0">
            {item.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

// Best Time Heatmap
const BestTimeHeatmap: React.FC<{
  data: number[][];
  days: string[];
  lessLabel: string;
  moreLabel: string;
}> = ({ data, days, lessLabel, moreLabel }) => {
  const hours = ['6h', '9h', '12h', '15h', '18h', '21h'];
  const maxValue = Math.max(...data.flat()) || 1;

  const getColor = (value: number) => {
    const intensity = value / maxValue;
    if (intensity < 0.2) return 'bg-indigo-100';
    if (intensity < 0.4) return 'bg-indigo-200';
    if (intensity < 0.6) return 'bg-indigo-300';
    if (intensity < 0.8) return 'bg-indigo-400';
    return 'bg-indigo-600';
  };

  return (
    <div>
      <div className="flex mb-2">
        <div className="w-8 sm:w-12" />
        {hours.map(hour => (
          <div key={hour} className="flex-1 text-center text-xs text-gray-500 dark:text-gray-400">{hour}</div>
        ))}
      </div>
      {data.map((row, dayIndex) => (
        <div key={dayIndex} className="flex items-center mb-1">
          <div className="w-8 sm:w-12 text-xs text-gray-500 dark:text-gray-400">{days[dayIndex]}</div>
          {row.map((value, hourIndex) => (
            <div key={hourIndex} className="flex-1 px-0.5">
              <div
                className={`h-6 sm:h-8 rounded ${getColor(value)} transition-colors cursor-pointer hover:ring-2 hover:ring-indigo-400`}
                title={`${days[dayIndex]} ${hours[hourIndex]}: ${value}`}
              />
            </div>
          ))}
        </div>
      ))}
      <div className="flex items-center justify-end gap-2 mt-4 text-xs text-gray-500 dark:text-gray-400">
        <span>{lessLabel}</span>
        <div className="flex gap-1">
          <div className="w-4 h-4 rounded bg-indigo-100" />
          <div className="w-4 h-4 rounded bg-indigo-200" />
          <div className="w-4 h-4 rounded bg-indigo-300" />
          <div className="w-4 h-4 rounded bg-indigo-400" />
          <div className="w-4 h-4 rounded bg-indigo-600" />
        </div>
        <span>{moreLabel}</span>
      </div>
    </div>
  );
};

const ContentAnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentProject } = useProject();

  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<SocialPlatform | 'all'>('all');

  // Analytics data state
  const [overviewStats, setOverviewStats] = useState({
    totalReach: 0,
    totalEngagement: 0,
    totalFollowers: 0,
    postsPublished: 0,
    reachChange: 0,
    engagementChange: 0,
    followersChange: 0,
    postsChange: 0
  });

  const [platformStats, setPlatformStats] = useState<{
    platform: SocialPlatform;
    followers: number;
    posts: number;
    engagement: number;
    reach: number;
    growth: number;
  }[]>([]);

  const [topPosts, setTopPosts] = useState<{
    id: string;
    platform: SocialPlatform;
    content: string;
    imageUrl?: string;
    likes: number;
    comments: number;
    shares: number;
    reach: number;
    publishedAt: Date;
  }[]>([]);

  const [engagementByType, setEngagementByType] = useState<{ label: string; value: number; color: string }[]>([]);
  const [bestTimeData, setBestTimeData] = useState<number[][]>([]);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      try {
        // Fetch real analytics data from backend
        const analyticsData = await contentHubService.getAnalytics(dateRange);

        // Set overview stats from API response
        if (analyticsData.overview) {
          setOverviewStats({
            totalReach: analyticsData.overview.totalReach || 0,
            totalEngagement: analyticsData.overview.totalEngagement || 0,
            totalFollowers: analyticsData.overview.totalFollowers || 0,
            postsPublished: analyticsData.overview.postsPublished || 0,
            reachChange: analyticsData.overview.reachChange || 0,
            engagementChange: analyticsData.overview.engagementChange || 0,
            followersChange: analyticsData.overview.followersChange || 0,
            postsChange: analyticsData.overview.postsChange || 0
          });
        }

        // Set platform stats from API
        if (analyticsData.platformMetrics && analyticsData.platformMetrics.length > 0) {
          setPlatformStats(analyticsData.platformMetrics.map((metric: any) => ({
            platform: metric.platform as SocialPlatform,
            followers: metric.followers || 0,
            posts: metric.posts || 0,
            engagement: metric.engagement || 0,
            reach: metric.reach || 0,
            growth: metric.growth || 0
          })));
        } else {
          setPlatformStats([]);
        }

        // Set top posts from API
        if (analyticsData.topPosts && analyticsData.topPosts.length > 0) {
          setTopPosts(analyticsData.topPosts.map((post: ContentItem) => {
            // Get metrics from analytics.platformMetrics if available
            const analytics = post.analytics;
            let totalLikes = 0, totalComments = 0, totalShares = 0, totalReach = 0;

            if (analytics?.platformMetrics) {
              for (const platform of post.platforms) {
                const metrics = analytics.platformMetrics[platform];
                if (metrics) {
                  totalLikes += metrics.likes || 0;
                  totalComments += metrics.comments || 0;
                  totalShares += metrics.shares || 0;
                  totalReach += metrics.reach || 0;
                }
              }
            }

            return {
              id: post.id,
              platform: post.platforms[0] || 'instagram',
              content: post.content.text,
              imageUrl: post.content.media[0]?.url,
              likes: totalLikes,
              comments: totalComments,
              shares: totalShares,
              reach: totalReach || analytics?.totalReach || 0,
              publishedAt: post.publishedAt || new Date()
            };
          }));
        } else {
          setTopPosts([]);
        }

        // Set engagement by type (calculated from overview or empty)
        if (analyticsData.overview?.engagementByType) {
          setEngagementByType(analyticsData.overview.engagementByType);
        } else {
          setEngagementByType([]);
        }

        // Set best time data (from API or empty matrix)
        if (analyticsData.overview?.bestTimeData) {
          setBestTimeData(analyticsData.overview.bestTimeData);
        } else {
          // Empty 7x6 matrix (7 days x 6 time slots)
          setBestTimeData(Array(7).fill(null).map(() => Array(6).fill(0)));
        }

      } catch (error) {
        console.error('Error fetching analytics:', error);
        // Reset to empty state on error
        setOverviewStats({
          totalReach: 0,
          totalEngagement: 0,
          totalFollowers: 0,
          postsPublished: 0,
          reachChange: 0,
          engagementChange: 0,
          followersChange: 0,
          postsChange: 0
        });
        setPlatformStats([]);
        setTopPosts([]);
        setEngagementByType([]);
        setBestTimeData(Array(7).fill(null).map(() => Array(6).fill(0)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [dateRange]);

  const handleExport = () => {
    // TODO: Implement export functionality
    alert('Funcionalidade de exportacao em desenvolvimento');
  };

  return (
    <div>
      {/* Header (dentro do layout: sem caixa branca extra, igual às outras telas) */}
      <div>
        <div className="max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <button
                onClick={() => navigate('/content')}
                aria-label={t('common.back', 'Voltar')}
                className="p-2 -ml-2 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-700 dark:text-gray-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {t('contentHub.analyticsTitle', 'Content Analytics')}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('contentHub.analyticsSubtitle', 'Track performance across all your social platforms')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Date Range Selector */}
              <div className="flex-1 md:flex-none grid grid-cols-3 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {(['7d', '30d', '90d'] as const).map(range => (
                  <button
                    key={range}
                    onClick={() => setDateRange(range)}
                    aria-pressed={dateRange === range}
                    className={`px-2 sm:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      dateRange === range
                        ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {t(`contentHub.analytics.dateRanges.${range}`, range === '7d' ? '7 days' : range === '30d' ? '30 days' : '90 days')}
                  </button>
                ))}
              </div>

              <Button
                variant="secondary"
                onClick={handleExport}
                aria-label={t('contentHub.analytics.export', 'Export')}
                title={t('contentHub.analytics.export', 'Export')}
                className="flex-shrink-0 whitespace-nowrap"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">{t('contentHub.analytics.export', 'Export')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl pt-4 sm:pt-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-8">
            {/* Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <StatCard
                title={t('contentHub.analytics.totalReach', 'Total Reach')}
                value={overviewStats.totalReach}
                change={overviewStats.reachChange}
                icon={<Eye className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />}
                color="bg-blue-50"
              />
              <StatCard
                title={t('contentHub.analytics.totalEngagement', 'Total Engagement')}
                value={overviewStats.totalEngagement}
                change={overviewStats.engagementChange}
                icon={<Heart className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />}
                color="bg-red-50"
              />
              <StatCard
                title={t('contentHub.analytics.totalFollowers', 'Total Followers')}
                value={overviewStats.totalFollowers}
                change={overviewStats.followersChange}
                icon={<Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />}
                color="bg-green-50"
              />
              <StatCard
                title={t('contentHub.analytics.postsPublished', 'Posts Published')}
                value={overviewStats.postsPublished}
                change={overviewStats.postsChange}
                icon={<BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />}
                color="bg-purple-50"
              />
            </div>

            {/* Platform Breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 sm:mb-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{t('contentHub.analytics.platformPerformance', 'Performance by Platform')}</h2>
                <select
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value as SocialPlatform | 'all')}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">{t('contentHub.analytics.allPlatforms', 'All Platforms')}</option>
                  {Object.values(PLATFORM_CONFIGS).map(config => (
                    <option key={config.id} value={config.id}>{config.name}</option>
                  ))}
                </select>
              </div>
              <div>
                {platformStats
                  .filter(p => selectedPlatform === 'all' || p.platform === selectedPlatform)
                  .map(stat => (
                    <PlatformStatsRow
                      key={stat.platform}
                      {...stat}
                      labels={{
                        followers: t('contentHub.analytics.followers', 'Followers'),
                        posts: t('contentHub.analytics.posts', 'Posts'),
                        engagement: t('contentHub.analytics.engagement', 'Engagement'),
                        reach: t('contentHub.analytics.reach', 'Reach'),
                        growth: t('contentHub.analytics.growth', 'Growth')
                      }}
                    />
                  ))}
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
              {/* Engagement by Type */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">{t('contentHub.analytics.engagementByType', 'Engagement by Type')}</h2>
                {engagementByType.length > 0 ? (
                  <SimpleBarChart data={engagementByType} />
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">{t('contentHub.analytics.noData', 'No data available')}</p>
                )}
              </div>

              {/* Best Time to Post */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">{t('contentHub.analytics.bestTimeToPost', 'Best Time to Post')}</h2>
                <BestTimeHeatmap
                  data={bestTimeData}
                  days={[
                    t('contentHub.analytics.days.sun', 'Sun'),
                    t('contentHub.analytics.days.mon', 'Mon'),
                    t('contentHub.analytics.days.tue', 'Tue'),
                    t('contentHub.analytics.days.wed', 'Wed'),
                    t('contentHub.analytics.days.thu', 'Thu'),
                    t('contentHub.analytics.days.fri', 'Fri'),
                    t('contentHub.analytics.days.sat', 'Sat')
                  ]}
                  lessLabel={t('contentHub.analytics.heatmapLess', 'Less')}
                  moreLabel={t('contentHub.analytics.heatmapMore', 'More')}
                />
              </div>
            </div>

            {/* Top Performing Posts */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-4 sm:mb-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white min-w-0">{t('contentHub.analytics.topPerformingPosts', 'Top Performing Posts')}</h2>
                <button
                  onClick={() => navigate('/content/all')}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1 whitespace-nowrap flex-shrink-0"
                >
                  {t('contentHub.analytics.viewAll', 'View all')}
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
              {topPosts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {topPosts.map(post => (
                    <TopPostCard key={post.id} post={post} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">{t('contentHub.analytics.noData', 'No data available')}</p>
              )}
            </div>

            {/* Insights Section */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white">
              <h2 className="text-lg font-semibold mb-4">{t('contentHub.analytics.insights.title', 'Insights & Recommendations')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5" />
                    <span className="font-medium">{t('contentHub.analytics.insights.growth', 'Growth')}</span>
                  </div>
                  <p className="text-sm text-white/80">
                    {t('contentHub.analytics.noDataDesc', 'Connect your accounts and publish content to see analytics')}
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5" />
                    <span className="font-medium">{t('contentHub.analytics.insights.bestTime', 'Best Time')}</span>
                  </div>
                  <p className="text-sm text-white/80">
                    {t('contentHub.analytics.noDataDesc', 'Connect your accounts and publish content to see analytics')}
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-5 h-5" />
                    <span className="font-medium">{t('contentHub.analytics.insights.engagement', 'Engagement')}</span>
                  </div>
                  <p className="text-sm text-white/80">
                    {t('contentHub.analytics.noDataDesc', 'Connect your accounts and publish content to see analytics')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentAnalyticsPage;
